import { useEffect, useRef, useState } from 'react'
import { isProductionEnv } from 'shared/config/config'
import { Handler } from 'shared/helpers/typeHelper'
import { styled } from 'styled-components'

import { useUserProvider } from '../Context/UserContext'
import { Spinner } from '../Spinner'

const DOCUSIGN_API = isProductionEnv()
  ? `https://js.docusign.com/bundle.js`
  : `https://js-d.docusign.com/bundle.js`
const jsWrapId = `docusign-script`
const iframeWrapId = `docusign-iframe`

interface DocusignProps {
  url: string
  integrationKey: string
  onAgree: Handler
  onError: Handler
  isOpen: boolean
}

let sdk_loaded = false

export function DocusignEnvelope({ url, integrationKey, onAgree, onError, isOpen }: DocusignProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSdkLoaded, setIsSdkLoaded] = useState(sdk_loaded)
  const { showBanner } = useUserProvider()
  const handleAgree = useRef(onAgree)

  useEffect(() => {
    handleAgree.current = onAgree
  })

  useEffect(() => {
    // So element will persist even if this component is unmounted, to finish the download
    if (!document.querySelector(`#${jsWrapId}`)) {
      const wrap = document.createElement('div')
      wrap.id = jsWrapId
      document.body.appendChild(wrap)
    }

    const script = document.createElement('script')
    script.src = DOCUSIGN_API
    script.async = true
    script.onload = () => {
      sdk_loaded = true
      setIsSdkLoaded(true)
    }
    document.body.appendChild(script)
  }, [])

  async function renderEnvelope() {
    ;(window as any).DocuSign.loadDocuSign(integrationKey)
      .then((docusign: any) => {
        const signing = docusign.signing({
          url,
          displayFormat: 'focused',
        })

        signing.on('sessionEnd', async (event: any) => {
          if (event.sessionEndType === 'signing_complete') {
            setIsLoading(true)
            await handleAgree.current()
            setIsLoading(false)
          } else {
            console.warn('sessionend', event)
            showBanner('error', { message: 'Something went wrong, please try again.' })
            onError()
          }
        })

        signing.mount(`#${iframeWrapId}`)
      })
      .catch((ex: any) => {
        console.error(ex)
        showBanner('error', { message: `Couldn't load the document. Please try again or contact us.` })
      })
  }

  useEffect(() => {
    if (isOpen && isSdkLoaded) {
      renderEnvelope()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSdkLoaded, isOpen])

  return (
    <>
      {isLoading && <Spinner open />}
      <Wrap id={iframeWrapId} />
    </>
  )
}

const Wrap = styled.div`
  height: 100%;
  min-height: 100%;
`
